import { useMemo } from 'react';

/**
 * useEventRelationships - Builds parent-child relationship maps for events
 *
 * This hook processes all events and creates efficient lookup maps for:
 * - Which events belong to which assignments
 * - Which matches belong to which group matches
 *
 * The hook handles edge cases like:
 * - Children arriving before parents (orphans)
 * - Parents arriving after children
 * - Out-of-order event delivery
 *
 * @param {Array} events - All events from the aggregator
 * @returns {Object} Relationship data
 *   - assignments: Map<assignmentId, Event[]> - Events grouped by assignment
 *   - groups: Map<groupId, Event[]> - Matches grouped by group_match
 *   - topLevelEvents: Event[] - Events that are not children of anything
 */
export function useEventRelationships(events) {
  return useMemo(() => {

    const assignments = new Map();
    const groups = new Map();
    const childEventIds = new Set();
    const parentEventIds = new Set();

    // First pass: Build set of parent IDs that actually exist in our events
    events.forEach(event => {
      if (event.eventType === 'assignment') {
        parentEventIds.add(event.eventId);
      }
      if (event.eventType === 'group_match') {
        parentEventIds.add(event.eventId);
      }
    });

    // Second pass: Build parent-child relationship maps (only if parent exists)
    events.forEach(event => {
      // Track events that belong to assignments
      if (event.metadata?.assignmentEventId) {
        const parentId = event.metadata.assignmentEventId;

        // Only treat as child if parent exists in our events
        if (parentEventIds.has(parentId)) {
          const children = assignments.get(parentId) || [];
          assignments.set(parentId, [...children, event]);
          childEventIds.add(event.eventId);
        } else {
          // Orphan: parent not in current event list, show as top-level
          console.log('[useEventRelationships] ORPHAN: Event', event.eventId?.substring(0, 15), 'references missing assignment', parentId.substring(0, 15));
        }
      }

      // Track matches that belong to group_match events
      if (event.eventType === 'match' && event.metadata?.groupEventId) {
        const parentId = event.metadata.groupEventId;

        // Only treat as child if parent exists in our events
        if (parentEventIds.has(parentId)) {
          const children = groups.get(parentId) || [];
          groups.set(parentId, [...children, event]);
          childEventIds.add(event.eventId);
        } else {
          // Orphan: parent not in current event list, show as top-level
          console.log('[useEventRelationships] ORPHAN: Event', event.eventId?.substring(0, 15), 'references missing group', parentId.substring(0, 15));
        }
      }
    });

    // Sort children within each assignment by timestamp (newest first)
    assignments.forEach((children, assignmentId) => {
      children.sort((a, b) => {
        const aTime = new Date(a.timestamp || a.createdAt).getTime();
        const bTime = new Date(b.timestamp || b.createdAt).getTime();
        return bTime - aTime;
      });
      assignments.set(assignmentId, children);
    });

    // Sort children within each group by timestamp (newest first)
    groups.forEach((children, groupId) => {
      children.sort((a, b) => {
        const aTime = new Date(a.timestamp || a.createdAt).getTime();
        const bTime = new Date(b.timestamp || b.createdAt).getTime();
        return bTime - aTime;
      });
      groups.set(groupId, children);
    });

    // Filter to get only top-level events (not children of anything)
    const topLevelEvents = events
      .filter(event => !childEventIds.has(event.eventId))
      .sort((a, b) => {
        const aTime = new Date(a.timestamp || a.createdAt).getTime();
        const bTime = new Date(b.timestamp || b.createdAt).getTime();
        return bTime - aTime;
      });

    return {
      assignments,
      groups,
      topLevelEvents
    };
  }, [events]);
}
